import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import router, {editEmployee, newEmployeePart, Route} from "@/routing/Router";
import {EmployeeDetails} from "@/data/entities/employee";
import AdminLayout from "@widgets/layout/AdminLayout";
import {useQuery} from "@tanstack/react-query";
import EmployeeEditor from "@widgets/employees/editor/EmployeeEditor";
import {SetStateFn} from "@/utils/SetStateUtils";
import {api} from "@/data/api";

const getOptionalEmployee = async (employeeId: string | null | undefined): Promise<EmployeeDetails | null> => {
    if (employeeId === undefined || employeeId === null || employeeId === newEmployeePart) {
        return null;
    }

    return api.employee.getEmployeeDetails(parseInt(employeeId));
};

export type SetEmployeeProps = {
    employee: EmployeeDetails | null,
    setEmployee: SetStateFn<EmployeeDetails | null>,
};

const Employee = () => {
    const {employeeId} = useParams();
    const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
    const {data, isLoading, error} = useQuery({
        queryKey: ["employee", employeeId || "new"],
        queryFn: () => getOptionalEmployee(employeeId),
    });
    const route: Route = useMemo(
        () => employee ? editEmployee(employee.id ?? -1) : router.newEmployee,
        [employee]);

    // Place fetched employee in state
    useEffect(() => {
        if (data !== undefined) {
            setEmployee(data);
        }
    }, [data]);

    // Trigger error page to show
    if (error) {
        throw new Error("Cannot load employee");
    }

    return (
        <AdminLayout currentPage={route}>
            <EmployeeEditor
                route={route}
                employee={employee}
                setEmployee={setEmployee}
                initialLoading={isLoading}
            />
        </AdminLayout>
    );
};

export default Employee;